/**
 * Purpose: Drives the horizontal scroll behavior of the `<vv-category-carousel>`
 *          ("Shop by Category") section - desktop prev/next arrows and the
 *          mobile-only (<750px) pagination dots that mirror the scroll position.
 *
 * Public API: the `<vv-category-carousel>` custom element. DOM contract:
 *   - [data-vv-cat-track]      the scrollable flex track (required)
 *   - [data-vv-cat-card]       a tile card (used to measure card width)
 *   - [data-vv-cat-prev]       previous arrow button (optional, desktop)
 *   - [data-vv-cat-next]       next arrow button (optional, desktop)
 *   - [data-vv-cat-dots]       dots container (optional, mobile)
 *   - [data-vv-cat-dot="<i>"]  a dot button; dataset index selects snap <i>
 *
 * Upstream deps: none - DOM/scroll APIs only, no framework base class, no imports.
 *
 * Downstream consumers: sections/vv-category-carousel.liquid (renders the markup
 *   and the dot buttons this element wires up).
 *
 * Failure modes: no-op when the track is absent. Dots are hidden when card width
 *   cannot be measured (cardWidth <= 0) or when there is no horizontal overflow
 *   (a single reachable snap). Metrics are re-derived from layout on every
 *   scroll/resize tick, so the element is idempotent and self-correcting across
 *   reflows - no cached geometry to invalidate.
 *
 * Performance: synchronous layout reads (getBoundingClientRect / scrollWidth /
 *   clientWidth) per scroll and resize tick; no I/O, no timers, no network.
 */
class VVCategoryCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-vv-cat-track]');
    this.prevBtn = this.querySelector('[data-vv-cat-prev]');
    this.nextBtn = this.querySelector('[data-vv-cat-next]');
    if (!this.track) return;

    this.dotsContainer = this.querySelector('[data-vv-cat-dots]');
    this.dots = this.dotsContainer ? Array.from(this.dotsContainer.querySelectorAll('[data-vv-cat-dot]')) : [];

    this.update = this.update.bind(this);
    this.prevBtn?.addEventListener('click', () => this.step(-1));
    this.nextBtn?.addEventListener('click', () => this.step(1));
    this.dotsContainer?.addEventListener('click', this.handleDotClick.bind(this));
    this.track.addEventListener('scroll', this.update, { passive: true });
    window.addEventListener('resize', this.update);
    this.update();
    this.updateDots();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.update);
  }

  getMetrics() {
    const card = this.track.querySelector('[data-vv-cat-card]');
    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    const cardWidth = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;
    const perView = Math.max(1, Math.floor(this.track.clientWidth / cardWidth));
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    return { cardWidth, perView, maxScroll };
  }

  step(direction) {
    const { cardWidth, perView } = this.getMetrics();
    this.track.scrollBy({ left: cardWidth * perView * direction, behavior: 'smooth' });
  }

  update() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth - 1;
    if (this.prevBtn) this.prevBtn.hidden = this.track.scrollLeft <= 0;
    if (this.nextBtn) this.nextBtn.hidden = this.track.scrollLeft >= maxScroll;
    this.updateDots();
  }

  updateDots() {
    if (!this.dots || !this.dots.length) return;
    const { cardWidth, maxScroll } = this.getMetrics();
    if (!(cardWidth > 0)) {
      this.dotsContainer.hidden = true;
      return;
    }
    // EPS mirrors update()'s `-1` slack; dots use (maxScroll + EPS), arrows use
    // (maxScroll - 1). Keep them numerically equal: this 1px split is deliberate -
    // do NOT "unify" the +1/-1 or you reintroduce a dead trailing dot on
    // fractional-column layouts.
    const EPS = 1;
    // FLOOR for COUNT: the track rests only at card-start snaps k*cardWidth
    // (scroll-snap-align: start); the largest reachable k is floor(maxScroll/cardWidth),
    // dots are 0..k -> +1. round() here would manufacture a dead dot.
    const reachable = Math.floor((maxScroll + EPS) / cardWidth) + 1;
    const snapCount = Math.min(this.dots.length, reachable);
    this.dotsContainer.hidden = snapCount <= 1;
    if (this.dotsContainer.hidden) return;
    // ROUND for ACTIVE: reading a settled snap position -> nearest snap. Clamp so
    // resting at maxScroll keeps the last dot active.
    const activeIndex = Math.min(Math.max(Math.round(this.track.scrollLeft / cardWidth), 0), snapCount - 1);
    this.dots.forEach((dot, index) => {
      dot.hidden = index >= snapCount;
      if (index === activeIndex) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
      dot.classList.toggle('is-active', index === activeIndex);
    });
  }

  handleDotClick(event) {
    const dot = event.target.closest?.('[data-vv-cat-dot]');
    if (!dot) return;
    const index = Number.parseInt(dot.dataset.vvCatDot, 10);
    if (Number.isNaN(index)) return;
    const { cardWidth, maxScroll } = this.getMetrics();
    if (!(cardWidth > 0)) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // The LAST dot targets maxScroll, not its card-start snap: on fractional-column
    // peek layouts floor(maxScroll/cardWidth)*cardWidth stops short of the track end
    // and leaves the final card clipped; maxScroll is a valid snap rest because
    // out-of-range start-aligned snaps clamp to the scroll bounds.
    const EPS = 1;
    const snapCount = Math.min(this.dots.length, Math.floor((maxScroll + EPS) / cardWidth) + 1);
    const target = index >= snapCount - 1 ? maxScroll : Math.min(index * cardWidth, maxScroll);
    this.track.scrollTo({ left: target, behavior: reduce ? 'auto' : 'smooth' });
  }
}

customElements.define('vv-category-carousel', VVCategoryCarousel);
